import warningIcon from "$assets/icons/warning-icon.svg?url";
import { Button } from "$components/button/button";
import { ConditionalDisplay } from "$components/conditional-display";
import { DialogBody } from "$components/dialog/dialog-body";
import { DialogContainer } from "$components/dialog/dialog-container";
import { DialogFooter } from "$components/dialog/dialog-footer";
import { DialogHeader } from "$components/dialog/dialog-header";
import { makeStyles } from "$components/mui-styles";
import { invoiceToExchangedValue } from "$routes/_lib/billing-v2/currency-exchange";
import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import { useCurrencyExchangeFormat } from "$routes/_lib/use-currency-exchange-format";
import ValidationContext from "$routes/_lib/validation/validation-context";
import { useLatestExchangeRate } from "$routes/patient/[:patientId]/_lib/invoices-v2/exchange-rate-provider";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Typography } from "@material-ui/core";
import type { BillingCurrency } from "elephant-web/gql";
import { useCallback, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import useCurrencyFormatter from "../../../hooks/useCurrencyFormatter";
import { ExchangeRateNote } from "../../ExchangeRateNote";
import { TRANSACTION_TOTAL, TRANSACTIONS } from "../components/formConstants";
import {
  amountLeftForRow,
  enteredByCurrency,
  exchangeForRate,
  hasExchangedRow,
  invoiceValuesOf,
  type PaymentRow,
  rowExchange,
} from "../components/payment-rows";
import { TotalError } from "../components/TotalError";
import { Transactions, useBaseTransaction } from "../components/Transactions";
import { Waive } from "../components/Waive";
import { useTakePayment } from "../hooks/useTakePayment";
import { useTransactionsSchema } from "./validationSchema";

const useStyles = makeStyles((theme) => ({
  modal: {
    borderRadius: theme.spacing(3),
    overflow: "scroll",
  },
  header: {
    display: "block",
  },
  totalAndWaivedWrapper: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  totalAmountContainer: {
    marginTop: theme.spacing(3),
  },
  totalAmountTitle: {
    fontSize: "12px",
  },
  totalAmount: {
    marginTop: "4px",
    fontSize: "24px",
    fontWeight: 700,
  },
  upperDivider: {
    marginTop: "15px",
  },
  errorTotal: {
    marginBottom: -theme.spacing(2),
    position: "relative",
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  paymentButton: {
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
      width: "100%",
    },
  },
  exemptButton: {
    marginRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
      width: "100%",
      marginRight: 0,
    },
  },
  footer: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      flexFlow: "column-reverse",
      justifycontent: "center",
      aligncontent: "flex-start",
      width: "100%",
      alignItems: "center",
    },
  },
}));

export const TakePaymentModal: FC<{
  open: boolean;
  onClose: () => void;
  invoiceId: string;
  currency: string;
  totalPayableInPence: number;
  waiveable: boolean;
  exemptable: boolean;
  openExempt: () => void;
}> = ({
  open,
  onClose,
  invoiceId,
  currency,
  totalPayableInPence,
  waiveable,
  exemptable,
  openExempt,
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { currencyFormatter } = useCurrencyFormatter();
  const { formatMoney, currencySymbol } = useCurrencyExchangeFormat();
  const baseTransaction = useBaseTransaction();
  const { takePayment } = useTakePayment(invoiceId);
  const [busy, setBusy] = useState(false);
  const latestRate = useLatestExchangeRate(currency as BillingCurrency);
  const penceToPounds = useConvertFromPenceFactory();
  const exchangeFor = useMemo(() => exchangeForRate(latestRate), [latestRate]);
  const currencyOptions = latestRate
    ? [currency, latestRate.otherCurrency]
    : undefined;

  const initialValues = useMemo(
    () => ({
      transactions: [
        baseTransaction({
          amountInPence: totalPayableInPence,
          currency,
        }),
      ],
      [TRANSACTION_TOTAL]: undefined,
    }),
    [baseTransaction, currency, totalPayableInPence],
  );

  const validationSchema = useTransactionsSchema({
    totalPayableInPence,
    minValue: 0,
    exchangeFor,
  });

  const formMethods = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onTouched",

    defaultValues: initialValues,
  });

  const watchedTransactions: PaymentRow[] =
    useWatch({ control: formMethods.control, name: TRANSACTIONS }) ?? [];
  const exchangeShown = watchedTransactions
    .map((row) => rowExchange(row, exchangeFor))
    .find(Boolean);
  const invoiceValues = invoiceValuesOf(
    watchedTransactions,
    totalPayableInPence,
    exchangeFor,
  );

  const setRowAmount = useCallback(
    (index: number, amountInPence: number) => {
      formMethods.setValue(
        `${TRANSACTIONS}.${index}.amountInPence`,
        amountInPence,
        {
          shouldValidate: true,
        },
      );
      formMethods.setValue(
        `${TRANSACTIONS}.${index}.amount`,
        penceToPounds(amountInPence),
        { shouldValidate: true },
      );
    },
    [formMethods, penceToPounds],
  );

  const handleCurrencyChange = useCallback(
    (index: number, rowCurrency: string) => {
      formMethods.setValue(`${TRANSACTIONS}.${index}.currency`, rowCurrency);
      setRowAmount(
        index,
        amountLeftForRow(
          formMethods.getValues(TRANSACTIONS),
          index,
          rowCurrency,
          totalPayableInPence,
          exchangeFor,
        ),
      );
      formMethods.trigger(TRANSACTIONS);
    },
    [formMethods, setRowAmount, totalPayableInPence, exchangeFor],
  );

  const handleWaiveChange = useCallback(() => {
    const rows = formMethods.getValues(TRANSACTIONS);
    setRowAmount(
      0,
      amountLeftForRow(
        rows,
        0,
        rows[0]?.currency ?? currency,
        totalPayableInPence,
        exchangeFor,
      ),
    );
    formMethods.trigger(TRANSACTIONS);
  }, [formMethods, setRowAmount, currency, totalPayableInPence, exchangeFor]);

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await takePayment({
        transactions: formMethods.getValues().transactions,
        currency,
        exchange: { exchangeFor, dueInPence: totalPayableInPence },
      });
    } finally {
      onClose();
    }
  };

  const paymentInvoiceTotal = invoiceValues.reduce(
    (total, value, i) =>
      watchedTransactions[i]?.type === "WAIVER" ? total : total + value,
    0,
  );
  const breakdown = enteredByCurrency(watchedTransactions, currency)
    .map(([code, value]) => formatMoney(code, value))
    .join(" + ");

  return (
    <DialogContainer
      open={open}
      className={classes.modal}
      data-testid="invoicing.takePaymentModal"
    >
      <ValidationContext.Provider value={{ validationSchema }}>
        <FormProvider {...formMethods}>
          <form
            name="take-payment-form"
            onSubmit={formMethods.handleSubmit(handleSubmit)}
          >
            <DialogHeader title="" className={classes.header}>
              <Box display="flex" alignItems="center">
                <img
                  src={warningIcon}
                  alt="warning"
                  className={classes.warningIcon}
                />
                <Typography variant="h5">
                  <FormattedMessage
                    id="invoicing.takePayment.title"
                    defaultMessage="Take Payment"
                  />
                </Typography>
              </Box>
              <div className={classes.totalAndWaivedWrapper}>
                <Box className={classes.totalAmountContainer}>
                  <span className={classes.totalAmountTitle}>
                    <FormattedMessage
                      id="invoicing.takePayment.totalAmountLabel"
                      defaultMessage="Total amount ({currency})"
                      values={{
                        currency,
                      }}
                    />
                  </span>
                  <div className={classes.totalAmount}>
                    {currencyFormatter(totalPayableInPence)}
                  </div>
                  {exchangeShown && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      data-testid="invoicing.takePayment.totalInOtherCurrency"
                    >
                      <FormattedMessage
                        id="invoicing.takePayment.totalInOtherCurrency"
                        defaultMessage="{amount} in {currency}"
                        values={{
                          amount: formatMoney(
                            exchangeShown.currency,
                            invoiceToExchangedValue(
                              totalPayableInPence,
                              exchangeShown.currency,
                              exchangeShown.rate,
                            ),
                          ),
                          currency: exchangeShown.currency,
                        }}
                      />
                    </Typography>
                  )}
                </Box>
                <ConditionalDisplay condition={waiveable}>
                  <Box>
                    <Waive
                      currency={currency}
                      totalPayableInPence={totalPayableInPence}
                      {...(latestRate && {
                        prefix: currencySymbol(currency),
                        onWaiveChange: handleWaiveChange,
                      })}
                    />
                  </Box>
                </ConditionalDisplay>
              </div>
            </DialogHeader>
            <Divider className={classes.upperDivider} />
            <DialogBody>
              <Grid container spacing={2}>
                {exchangeShown && (
                  <Grid item xs={12}>
                    <ExchangeRateNote rate={exchangeShown.rate} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Transactions
                    currency={currency}
                    currencyOptions={currencyOptions}
                    invoiceValues={invoiceValues}
                    onCurrencyChange={handleCurrencyChange}
                    disabled={busy}
                  />
                </Grid>
                <Grid item xs={12} className={classes.errorTotal}>
                  <TotalError
                    totalPayableInPence={totalPayableInPence}
                    exchangeFor={exchangeFor}
                  />
                </Grid>
              </Grid>
            </DialogBody>
            <Divider />
            <DialogFooter>
              <Box className={classes.footer}>
                <Box>
                  <Button
                    className={classes.paymentButton}
                    color="secondary"
                    onClick={onClose}
                    disabled={busy}
                  >
                    <FormattedMessage
                      id="invoicing.takePayment.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                </Box>
                <ConditionalDisplay condition={exemptable}>
                  <Button
                    onClick={openExempt}
                    color="secondary"
                    data-testid="invoicing.takePayment.exemptButton"
                    className={classes.exemptButton}
                    disabled={busy}
                  >
                    <FormattedMessage
                      data-testid="invoicing.takePayment.exempt"
                      id="invoicing.takePayment.exempt"
                      defaultMessage="Exempt"
                    />
                  </Button>
                </ConditionalDisplay>
                <Button
                  className={classes.paymentButton}
                  type="submit"
                  color="primary"
                  data-testid="invoicing.takePayment.confirmButton"
                  loading={busy}
                  disabled={
                    formMethods.formState.isSubmitting ||
                    !formMethods.formState.isValid
                  }
                >
                  {formMethods.formState.isValid &&
                  hasExchangedRow(watchedTransactions, exchangeFor) ? (
                    <FormattedMessage
                      id="invoicing.takePayment.confirmExchanged"
                      defaultMessage="Take Payment {exchanged} ({amount})"
                      values={{
                        exchanged: breakdown,
                        amount: currencyFormatter(paymentInvoiceTotal),
                      }}
                    />
                  ) : (
                    <FormattedMessage
                      data-testid="invoices.takePayment.confirm"
                      id="invoicing.takePayment.confirm"
                      defaultMessage="Take Payment"
                    />
                  )}
                </Button>
              </Box>
            </DialogFooter>
          </form>
        </FormProvider>
      </ValidationContext.Provider>
    </DialogContainer>
  );
};
